/**
 * Session Recorder for PWA
 * 
 * Records user sessions using rrweb for error debugging.
 * GDPR-compliant: All PII is masked before upload.
 * 
 * Usage:
 *   import { sessionRecorder } from '@/lib/sessionRecorder';
 *   
 *   // Start recording at payment flow start
 *   sessionRecorder.startRecording();
 *   
 *   // On error, upload the recording
 *   await sessionRecorder.stopAndUpload(criticalEventId);
 *   
 *   // On success, discard the recording
 *   sessionRecorder.discardRecording();
 */

import { supabase } from './supabase';
import { APP_CONFIG } from './app-config';

// Type for rrweb events (compatible with rrweb 2.x)
interface RRWebEvent {
  type: number;
  timestamp: number;
  data: unknown;
}

// GDPR: Elements to block completely
const MASKED_SELECTORS = [
  '[data-pii]',
  '[data-sensitive]',
  '.pii',
  '.sensitive',
  '.personal-number',
  '.phone-input',
  '.email-input',
  '.bank-id',
];

// Max recording duration (5 minutes sliding window)
const MAX_RECORDING_DURATION_MS = 5 * 60 * 1000;

// Max events to keep in buffer
const MAX_EVENTS = 5000;

interface SessionRecorderState {
  isRecording: boolean;
  events: RRWebEvent[];
  startTime: number | null;
  stopFn: (() => void) | null;
  pendingStart: boolean;
}

class SessionRecorder {
  private state: SessionRecorderState = {
    isRecording: false,
    events: [],
    startTime: null,
    stopFn: null,
    pendingStart: false,
  };

  /**
   * Start recording the session
   * Automatically masks PII and limits buffer size
   *
   * Uses macrotask isolation (setTimeout) to ensure rrweb's MutationObserver
   * never starts during React's render/commit cycle. This is the standard
   * pattern for integrating third-party DOM libraries with React.
   */
  async startRecording(): Promise<void> {
    if (this.state.isRecording || this.state.pendingStart) {
      return;
    }

    this.state.pendingStart = true;

    try {
      const { record } = await import('rrweb');

      if (!this.state.pendingStart) {
        return;
      }

      // Isolate rrweb initialization in a macrotask so its MutationObserver
      // never starts inside React's synchronous render/commit cycle.
      // Without this, rrweb errors propagate to React as error #310.
      await new Promise<void>((resolve) => {
        setTimeout(() => {
          if (!this.state.pendingStart) {
            resolve();
            return;
          }

          try {
            this.state.events = [];
            this.state.startTime = Date.now();
            this.state.isRecording = true;

            const stopFn = record({
              emit: (event: RRWebEvent) => {
                this.state.events.push(event);

                if (this.state.events.length > MAX_EVENTS) {
                  this.state.events = this.state.events.slice(-MAX_EVENTS);
                }

                const now = Date.now();
                if (this.state.startTime && now - this.state.startTime > MAX_RECORDING_DURATION_MS) {
                  const cutoffTime = now - MAX_RECORDING_DURATION_MS;
                  const cutoffIndex = this.state.events.findIndex(e => e.timestamp >= cutoffTime);
                  if (cutoffIndex > 0) {
                    this.state.events = this.state.events.slice(cutoffIndex);
                  }
                }
              },
              maskAllInputs: true,
              sampling: {
                scroll: 150,
                input: 'last' as const,
                mousemove: false,
              },
              recordCanvas: false,
              inlineStylesheet: true,
              collectFonts: false,
            });

            this.state.stopFn = stopFn ?? null;
            this.state.pendingStart = false;
            console.log('[SessionRecorder] Started recording');
          } catch (err) {
            console.error('[SessionRecorder] record() failed:', err);
            this.state.isRecording = false;
            this.state.pendingStart = false;
          }

          resolve();
        }, 0);
      });
    } catch (err) {
      console.error('[SessionRecorder] Failed to start recording:', err);
      this.state.isRecording = false;
      this.state.pendingStart = false;
    }
  }

  /**
   * Stop recording and upload to Supabase storage
   * Links the recording to a critical event
   * Includes retry logic with exponential backoff
   * 
   * @param criticalEventId - The critical event to link the recording to
   * @param guestSessionId - Optional guest session ID for guest purchases
   */
  async stopAndUpload(criticalEventId: string, guestSessionId?: string): Promise<string | null> {
    if (!this.state.isRecording) {
      console.log('[SessionRecorder] Not recording');
      return null;
    }

    try {
      // Stop recording
      if (this.state.stopFn) {
        this.state.stopFn();
        this.state.stopFn = null;
      }
      this.state.isRecording = false;

      if (this.state.events.length === 0) {
        console.log('[SessionRecorder] No events to upload');
        return null;
      }

      // Calculate duration
      const durationMs = this.state.events.length > 1
        ? this.state.events[this.state.events.length - 1].timestamp - this.state.events[0].timestamp
        : 0;

      // Prepare metadata
      const metadata = {
        duration_ms: durationMs,
        event_count: this.state.events.length,
        masked_fields: MASKED_SELECTORS,
        recorded_at: new Date().toISOString(),
        user_agent: navigator.userAgent,
        isGuest: !!guestSessionId,
      };

      // Compress events using pako
      const { gzip } = await import('pako');
      const eventsJson = JSON.stringify(this.state.events);
      const compressed = gzip(eventsJson);

      // Check for auth token
      const session = await supabase.auth.getSession();
      const token = session.data?.session?.access_token;
      
      // Determine upload endpoint and headers
      const isGuestUpload = !token && !!guestSessionId;
      const uploadEndpoint = isGuestUpload 
        ? `${APP_CONFIG.supabase.functionsUrl}/api-session-replay/upload-guest`
        : `${APP_CONFIG.supabase.functionsUrl}/api-session-replay/upload`;
      
      // Build headers based on upload type
      const headers: Record<string, string> = {
        'Content-Type': 'application/octet-stream',
        'X-Critical-Event-Id': criticalEventId,
        'X-Metadata': JSON.stringify(metadata),
      };
      
      if (isGuestUpload) {
        headers['X-Guest-Session-Id'] = guestSessionId!;
        console.log('[SessionRecorder] Uploading as guest', { criticalEventId, guestSessionId });
      } else if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        console.log('[SessionRecorder] Uploading as authenticated user', { criticalEventId });
      } else {
        console.error('[SessionRecorder] No auth token and no guest session - cannot upload');
        this.state.events = [];
        return null;
      }

      // Retry logic with exponential backoff
      const uploadWithRetry = async (attempt = 1): Promise<Response> => {
        const maxAttempts = 3;
        try {
          const response = await fetch(uploadEndpoint, {
            method: 'POST',
            headers,
            body: compressed,
          });

          // Retry on server errors or rate limiting
          if (!response.ok && attempt < maxAttempts) {
            const shouldRetry = response.status >= 500 || response.status === 429;
            if (shouldRetry) {
              const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000); // 1s, 2s, 4s max 5s
              console.log(`[SessionRecorder] Retry attempt ${attempt + 1} after ${delay}ms`);
              await new Promise(r => setTimeout(r, delay));
              return uploadWithRetry(attempt + 1);
            }
          }
          return response;
        } catch (err) {
          if (attempt < maxAttempts) {
            const delay = Math.min(1000 * Math.pow(2, attempt - 1), 5000);
            console.log(`[SessionRecorder] Network error, retry ${attempt + 1} after ${delay}ms`);
            await new Promise(r => setTimeout(r, delay));
            return uploadWithRetry(attempt + 1);
          }
          throw err;
        }
      };

      const response = await uploadWithRetry();
      const result = await response.json();
      
      if (!result.ok) {
        console.error('[SessionRecorder] Upload failed:', result.error);
        return null;
      }

      console.log('[SessionRecorder] Upload successful:', result.path, isGuestUpload ? '(guest)' : '(authenticated)');
      
      // Clear events after successful upload
      this.state.events = [];
      
      return result.path;
    } catch (err) {
      console.error('[SessionRecorder] Failed to upload:', err);
      // Clear events even on failure to prevent memory leak
      this.state.events = [];
      return null;
    }
  }

  /**
   * Discard the current recording without uploading
   * Use this on successful payment completion
   */
  discardRecording(): void {
    if (this.state.stopFn) {
      this.state.stopFn();
      this.state.stopFn = null;
    }
    this.state.isRecording = false;
    this.state.pendingStart = false; // Cancel any in-flight import
    this.state.events = [];
    this.state.startTime = null;
    console.log('[SessionRecorder] Recording discarded');
  }

  /**
   * Check if currently recording or pending start
   */
  isRecording(): boolean {
    return this.state.isRecording || this.state.pendingStart;
  }

  /**
   * Get current event count
   */
  getEventCount(): number {
    return this.state.events.length;
  }
}

// Singleton instance
export const sessionRecorder = new SessionRecorder();
